/** How a promotion stage reads on screen, and what a protocol's rollup adds up to. */

import type { ProtocolRollup, PromotionJob, TypePromotionStage } from './types';

export const STAGE_LABELS: Record<TypePromotionStage, string> = {
	untested: 'Not tested',
	not_ready: 'Not ready',
	stale: 'Stale',
	ready: 'Ready',
	templated: 'Template drafted',
	promoted: 'Promoted'
};

export const STAGE_VARIANTS: Record<TypePromotionStage, 'secondary' | 'destructive' | 'warning' | 'success' | 'info' | 'default'> = {
	untested: 'secondary',
	not_ready: 'destructive',
	stale: 'warning',
	ready: 'success',
	templated: 'info',
	promoted: 'default'
};

/** The order a type moves through; also the order filters and the rollup bar list them in. */
export const STAGE_ORDER: TypePromotionStage[] = ['promoted', 'templated', 'ready', 'stale', 'not_ready', 'untested'];

const ROLLUP_KEYS: Record<TypePromotionStage, keyof Omit<ProtocolRollup, 'types'>> = {
	promoted: 'promoted',
	templated: 'templated',
	ready: 'ready',
	stale: 'stale',
	not_ready: 'notReady',
	untested: 'untested'
};

export const countOf = (rollup: ProtocolRollup, stage: TypePromotionStage): number => rollup[ROLLUP_KEYS[stage]];

/** One line for a protocol row: "9 of 12 types ready or further · 7 promoted". */
export const rollupSummary = (rollup: ProtocolRollup): string => {
	if (!rollup.types) return 'No scenario types';
	const cleared = rollup.ready + rollup.templated + rollup.promoted;
	const parts = [`${cleared} of ${rollup.types} types cleared`];
	if (rollup.promoted) parts.push(`${rollup.promoted} promoted`);
	return parts.join(' · ');
};

/** A job still being worked on. */
export const isJobActive = (job: PromotionJob | null | undefined): boolean =>
	job?.status === 'queued' || job?.status === 'running';

/** Types the job has settled, of those it took: "3 of 8". */
export const jobProgress = (job: PromotionJob): { settled: number; total: number } => ({
	settled: job.types.filter((type) => type.status === 'done' || type.status === 'failed').length,
	total: job.types.length
});
